import React, { useState, useContext } from 'react';
import { UserContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  Download, 
  Upload, 
  Users,
  Key,
  Clock,
  AlertTriangle,
  CheckCircle,
  Database
} from 'lucide-react';

const UserBackupManager = () => {
  const { user, API } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [backupData, setBackupData] = useState(null);

  const downloadUserBackup = async () => {
    try {
      setLoading(true);
      toast.info('Création de la sauvegarde des utilisateurs...');
      
      const response = await axios.get(`${API}/system/backup-users?user_role=${user.role}`);
      
      setBackupData(response.data);
      
      // Créer et télécharger le fichier
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_backup_${new Date().toISOString().split('T')[0]}_${new Date().toTimeString().split(' ')[0].replace(/:/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Sauvegarde des utilisateurs créée et téléchargée !');
      
    } catch (error) {
      console.error('Error backing up users:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé - Administrateur système requis');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const ensureCriticalAdmins = async () => {
    try {
      const response = await axios.post(`${API}/system/ensure-critical-admins`);
      
      toast.success('Comptes administrateurs critiques garantis !');
      
      // Afficher les identifiants
      toast.info(`admin: ${response.data.credentials.admin}`, { duration: 10000 });
      toast.info(`ETTI: ${response.data.credentials.etti}`, { duration: 10000 });
      
    } catch (error) {
      console.error('Error ensuring critical admins:', error);
      toast.error('Erreur lors de la vérification des comptes critiques');
    }
  };

  const restoreUsers = async () => {
    try {
      if (!window.confirm('⚠️ ATTENTION: Restaurer les utilisateurs peut écraser les données existantes. Continuer ?')) {
        return;
      }
      
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              const backupData = JSON.parse(e.target.result);
              
              const response = await axios.post(`${API}/system/restore-users?user_role=${user.role}`, backupData);
              
              toast.success(`Utilisateurs restaurés : ${response.data.restored_count}/${response.data.total_in_backup}`);
              
            } catch (error) {
              toast.error('Erreur lors de la restauration ou fichier invalide');
            }
          };
          reader.readAsText(file);
        }
      };
      input.click();
      
    } catch (error) {
      console.error('Error restoring users:', error);
      toast.error('Erreur lors de la restauration');
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString('fr-FR');
    } catch {
      return 'Date invalide';
    }
  };

  if (!user || user.role !== 'system_admin') {
    return (
      <div className="bg-red-50 p-4 rounded-lg border border-red-200 max-w-md mx-auto mt-8">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="font-medium text-red-800">Accès Refusé</p>
        </div>
        <p className="text-sm text-red-700 mt-2">
          Seul l'administrateur système peut gérer les sauvegardes d'utilisateurs.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sauvegarde des Utilisateurs */}
      <Card className="bg-white/70 glass border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Sauvegarde des Utilisateurs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Database className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800 mb-1">
                  Données sauvegardées dans le fichier :
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Tous les comptes utilisateurs avec hashs de mots de passe</li>
                  <li>• Rôles et permissions de chaque utilisateur</li>
                  <li>• Statuts d'approbation et dates de création</li>
                  <li>• Identifiants des comptes administrateurs critiques</li>
                  <li>• Instructions de restauration complètes</li>
                </ul>
              </div>
            </div>
          </div>

          <Button
            onClick={downloadUserBackup}
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-lg"
          >
            {loading ? (
              <>
                <div className="spinner mr-3"></div>
                Création de la sauvegarde...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-3" />
                Télécharger la Sauvegarde Complète des Utilisateurs
              </>
            )}
          </Button>

          {backupData && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <p className="font-medium text-green-800">Sauvegarde créée avec succès</p>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <p><strong>Date :</strong> {formatTimestamp(backupData.backup_timestamp)}</p>
                <p><strong>Utilisateurs sauvegardés :</strong> {backupData.total_users}</p>
                <p><strong>Comptes critiques :</strong> {backupData.critical_accounts.length}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restauration d'Urgence */}
      <Card className="bg-orange-50 border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Upload className="h-5 w-5" />
            Restauration d'Urgence
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-orange-100 p-4 rounded-lg border border-orange-300">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800 mb-1">
                  Utilisez en cas de perte d'accès post-déploiement
                </p>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Garantit que les comptes admin et ETTI existent</li>
                  <li>• Recrée les mots de passe avec les valeurs par défaut</li>
                  <li>• Aucune perte de données utilisateur</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={ensureCriticalAdmins}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Key className="h-4 w-4 mr-2" />
              Garantir Comptes Critiques
            </Button>
            
            <Button
              onClick={restoreUsers}
              variant="outline"
              className="border-orange-300 text-orange-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Restaurer depuis Fichier
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comptes Critiques */}
      <Card className="bg-red-50 border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Shield className="h-5 w-5" />
            Comptes Administrateurs Critiques
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-white rounded-lg border">
              <h4 className="font-medium text-gray-900 mb-2">📋 Identifiants de Récupération :</h4>
              
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm"><strong>admin</strong> / Mboulangerie95</p>
                      <Badge className="bg-red-100 text-red-800 text-xs mt-1">System Admin</Badge>
                    </div>
                    <Badge variant="outline">Compte Principal</Badge>
                  </div>
                </div>
                
                <div className="p-3 bg-gray-50 rounded border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm"><strong>ETTI</strong> / ETTI95</p>
                      <Badge className="bg-red-100 text-red-800 text-xs mt-1">System Admin</Badge>
                    </div>
                    <Badge variant="outline">Compte Personnel</Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
              <p className="text-xs font-medium text-yellow-800">
                💡 Ces comptes sont automatiquement recréés au démarrage de l'application si ils n'existent pas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserBackupManager;